import { useEffect, useRef, useState } from "react";
import { PlayerAvatar } from "@client/components/player-avatar";
import type { PlayerProjection } from "@saboteur/contracts";
import "@client/components/personal-card.css";

export function RoleDetails({
  role,
  resetKey,
  reminderKey,
}: {
  role: PlayerProjection["self"]["role"];
  resetKey: string;
  reminderKey: string;
}) {
  const storageKey = `saboteur:seen-role:${reminderKey}`;
  const [seenAssignment, setSeenAssignment] = useState<string | null>(() => {
    try {
      return localStorage.getItem(storageKey);
    } catch {
      return null;
    }
  });
  const needsReveal = Boolean(role) && seenAssignment !== resetKey;
  const promptId = "role-reminder";
  useEffect(() => {
    try {
      setSeenAssignment(localStorage.getItem(storageKey));
    } catch {
      setSeenAssignment(null);
    }
  }, [storageKey]);
  const roleDialog = useRef<HTMLDialogElement>(null);
  const roleButton = useRef<HTMLButtonElement>(null);
  const [roleRevealed, setRoleRevealed] = useState(false);
  const [roleView, setRoleView] = useState(0);
  useEffect(() => {
    setRoleRevealed(false);
    if (roleDialog.current?.open) roleDialog.current.close();
  }, [resetKey]);
  useEffect(() => {
    const hide = () => {
      if (document.hidden) setRoleRevealed(false);
    };
    document.addEventListener("visibilitychange", hide);
    return () => document.removeEventListener("visibilitychange", hide);
  }, []);

  return (
    <>
      <button
        ref={roleButton}
        type="button"
        className={`table-icon-button role-button ${needsReveal ? "role-button--unseen" : ""}`}
        aria-describedby={needsReveal ? promptId : undefined}
        aria-label="Your role"
        title="Your role"
        onClick={() => {
          setRoleRevealed(false);
          setRoleView((view) => view + 1);
          roleDialog.current?.showModal();
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <rect x="6" y="3" width="13" height="18" rx="2" />
          <path d="m12.5 8 3.5 4-3.5 4-3.5-4Z" />
          <path d="M3 6v12" strokeLinecap="round" />
        </svg>
        {needsReveal && (
          <span className="role-button__badge" aria-hidden="true">
            !
          </span>
        )}
      </button>
      {needsReveal && (
        <span id={promptId} className="role-reminder" role="status">
          Check your role
        </span>
      )}
      <dialog
        ref={roleDialog}
        className="personal-card"
        aria-labelledby="personal-role-title"
        onClose={() => {
          setRoleRevealed(false);
          roleButton.current?.focus();
        }}
        onCancel={() => setRoleRevealed(false)}
      >
        <button
          type="button"
          className="personal-card__close"
          aria-label="Close personal details"
          onClick={() => roleDialog.current?.close()}
        >
          ×
        </button>
        <h2 id="personal-role-title">Your role</h2>
        <div className="role-flip" key={roleView}>
          <button
            type="button"
            className="role-flip__toggle"
            aria-label={roleRevealed ? "Hide role" : "Reveal role"}
            aria-pressed={roleRevealed}
            onClick={() => {
              if (!roleRevealed && role) {
                setSeenAssignment(resetKey);
                try {
                  localStorage.setItem(storageKey, resetKey);
                } catch {
                  /* Keep the reminder dismissed in memory when storage is unavailable. */
                }
              }
              setRoleRevealed((revealed) => !revealed);
            }}
          />
          <div className="role-flip__card" data-revealed={roleRevealed}>
            <div className="role-flip__back" aria-hidden={roleRevealed}>
              <span className="role-flip__emblem" aria-hidden="true">
                ◇
              </span>
              <strong>Tap to reveal</strong>
            </div>
            <div className="role-flip__front" aria-hidden={!roleRevealed}>
              {roleRevealed && role ? (
                <>
                  <PlayerAvatar seat={role === "MINER" ? 4 : 9} />
                  <h3>{role === "MINER" ? "Gold miner" : "Saboteur"}</h3>
                  <p className="personal-card__objective">
                    {role === "MINER"
                      ? "Build a path to the gold."
                      : "Stop the crew reaching the gold."}
                  </p>
                  <small className="role-flip__hint">Tap to hide</small>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </dialog>
    </>
  );
}
